import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, PlayCircle, Loader2, History, ListChecks } from "lucide-react";

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
  } catch { return "—"; }
};

const Stat = ({ label, value, testid }) => (
  <div className="surface-card p-4">
    <div className="overline">{label}</div>
    <div className="mt-1 text-2xl font-display font-semibold" data-testid={testid}>{value}</div>
  </div>
);

export default function AdminReconciliation() {
  const [job, setJob] = useState(null);
  const [backlog, setBacklog] = useState({ payout: 0, bbps: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [batch, setBatch] = useState(50);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/reconciliation/status"),
        api.get("/admin/reconciliation/backlog"),
      ]);
      setJob(s.data.job || null);
      setBacklog(b.data || { payout: 0, bbps: 0, total: 0 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load reconciliation state");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll while a sweep is in-flight so the UI feels live.
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const runNow = async (type) => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/reconciliation/run", null, {
        params: { batch, ...(type ? { type } : {}) },
      });
      const r = data.result || {};
      toast.success(`Swept ${r.processed} · enriched ${r.enriched || 0} · errors ${r.errors || 0}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sweep failed");
    } finally {
      setRunning(false);
    }
  };

  const isRunning = job?.running || running;

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin · Reconciliation jobber</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Provider status reconciliation
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            A separate background jobber walks every settled <span className="font-mono">success / failed</span> transaction
            missing a <span className="font-mono">payout_last_status_response</span> snapshot, pulls live status from the
            provider, and backfills UTR / operator-ref / dateAdded. Runs automatically on a slow cadence — or kick a sweep
            manually below.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="reload-reconciliation">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
          Reload
        </Button>
      </div>

      <div className="mt-6 grid sm:grid-cols-4 gap-3">
        <Stat label="Backlog · Payout" value={backlog.payout} testid="backlog-payout" />
        <Stat label="Backlog · BBPS" value={backlog.bbps} testid="backlog-bbps" />
        <Stat label="Total enriched (lifetime)" value={job?.enriched_total ?? 0} testid="enriched-total" />
        <Stat label="Total errors (lifetime)" value={job?.errors_total ?? 0} testid="errors-total" />
      </div>

      <div className="mt-6 surface-card p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <span className="overline">Manual sweep</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Fires one reconciliation pass right now. The jobber serialises sweeps — if one's already running, this'll wait.
            </p>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label htmlFor="batch-size" className="text-xs">Batch size (per type)</Label>
              <Input
                id="batch-size"
                data-testid="reconciliation-batch"
                type="number"
                min={1}
                max={200}
                value={batch}
                onChange={(e) => setBatch(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
                className="mt-1 w-28 font-mono"
              />
            </div>
            <Button
              onClick={() => runNow(null)}
              disabled={isRunning}
              data-testid="reconciliation-run-all"
            >
              {isRunning ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" strokeWidth={1.5} />
              ) : (
                <PlayCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
              )}
              Sweep all
            </Button>
            <Button
              onClick={() => runNow("payout")}
              disabled={isRunning}
              variant="outline"
              data-testid="reconciliation-run-payout"
            >
              Payouts only
            </Button>
            <Button
              onClick={() => runNow("bbps")}
              disabled={isRunning}
              variant="outline"
              data-testid="reconciliation-run-bbps"
            >
              BBPS only
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6 surface-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <span className="overline">Jobber state</span>
          {job?.running && (
            <Badge className="bg-warning/15 text-warning border border-warning/30 ml-2" data-testid="jobber-running-badge">
              <Loader2 className="h-3 w-3 mr-1 animate-spin" strokeWidth={2} /> Running
            </Badge>
          )}
        </div>
        {!job ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Last run started</dt><dd className="font-mono" data-testid="last-run-at">{fmtIST(job.last_run_at)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Last run finished</dt><dd className="font-mono">{fmtIST(job.last_finished_at)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Last duration</dt><dd className="font-mono">{job.last_run_duration_seconds}s</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Processed (lifetime)</dt><dd className="font-mono">{job.processed_total}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Skipped (lifetime)</dt><dd className="font-mono">{job.skipped_total}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Last error</dt><dd className="font-mono text-destructive truncate max-w-[220px]" title={job.last_error || ""}>{job.last_error || "—"}</dd></div>
          </dl>
        )}
        {job?.current_run && (
          <div className="mt-4 border border-warning/30 bg-warning/5 rounded-md p-3 text-xs">
            <div className="font-semibold mb-1">Active sweep</div>
            <div className="grid sm:grid-cols-4 gap-2 font-mono">
              <div>started: {fmtIST(job.current_run.started_at)}</div>
              <div>processed: {job.current_run.processed}</div>
              <div>enriched: {job.current_run.enriched}</div>
              <div>errors: {job.current_run.errors}</div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
